import { useEffect, useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { EmptyState, SkeletonCardGrid } from "@/components/base/StateViews";
import WorkCard from "@/components/feature/WorkCard";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import FilterBar, { type FilterGroup } from "@/pages/category/components/FilterBar";
import { collectGenres, collectRegions, collectYears, filterWorks } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

const PAGE_SIZE = 8;
const sortOptions = ["最新", "热门"];

export default function MovieCategory() {
  useSeo({
    title: "电影分类 - 亚洲天堂",
    description:
      "亚洲天堂电影分类频道，汇聚动作、爱情、科幻、悬疑等各类电影作品与最新资讯，支持按类型、地区、年份筛选，帮助影迷快速找到想看的电影。",
    keywords: "电影分类,电影资讯,最新电影,亚洲天堂电影,电影推荐",
  });

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("最新");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const genres = useMemo(() => collectGenres("movie"), []);
  const regions = useMemo(() => collectRegions("movie"), []);
  const years = useMemo(() => collectYears("movie"), []);

  const list: Work[] = useMemo(
    () =>
      filterWorks({
        type: "movie",
        genre,
        region,
        year,
        sort: sort === "热门" ? "hot" : "latest",
      }),
    [genre, region, year, sort],
  );

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const groups: FilterGroup[] = [
    { key: "genre", label: "类型", options: genres, value: genre },
    { key: "region", label: "地区", options: regions, value: region },
    { key: "year", label: "年份", options: years, value: year },
    { key: "sort", label: "排序", options: sortOptions, value: sort },
  ];

  const handleChange = (key: string, value: string) => {
    if (key === "genre") setGenre(value);
    if (key === "region") setRegion(value);
    if (key === "year") setYear(value);
    if (key === "sort") setSort(value);
  };

  const handleReset = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("最新");
  };

  return (
    <div className="animate-fade-in">
      <CategoryHeader
        title="电影分类"
        description="从院线新片到经典佳作，按类型、地区与年份轻松筛选，发现属于你的下一部电影。"
        icon="ri-film-line"
        breadcrumb={<Breadcrumb items={[{ label: "电影" }]} />}
      />

      <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        <FilterBar groups={groups} onChange={handleChange} onReset={handleReset} />

        {loading ? (
          <SkeletonCardGrid count={PAGE_SIZE} />
        ) : list.length === 0 ? (
          <EmptyState
            title="暂无符合条件的电影"
            description="试试调整或重置筛选条件，也许会有更多发现。"
            actionLabel="重置筛选"
            onAction={handleReset}
            icon="ri-film-line"
          />
        ) : (
          <>
            <div className="mb-4 text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{list.length}</strong> 部电影
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} showFavorite />
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}